import { Link } from 'react-router-dom';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import * as Meter from '@givebutter/primitives/meter';
import { maskWholeNumber } from '@givebutter/util';
import { PLUS_MARKETING_LINK } from 'constants/links';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDataHygieneMetrics } from 'customHooks/useDataHygieneMetrics';
import DataHygieneFeature from 'images/data-hygiene-feature.webp';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';

interface TrialProgressCardProps {
    total: number;
    progress: number;
    visibleTotal: number;
}

const TrialProgressCard = ({ total = 100, progress = 0, visibleTotal }: TrialProgressCardProps) => {
    const { accountId, isCurrentlyOnFreeTrial } = useCurrentAccount();
    const { data: metrics } = useDataHygieneMetrics();
    const isDataClean = metrics.data_hygiene.total === 0; // NOTE: this total does not include duplicates count
    const isTotalReviewed = progress < total;

    return isCurrentlyOnFreeTrial ? (
        <Box className="overflow-hidden p-0">
            <div className="@container flex w-full gap-8 border-b border-gray-200 p-6">
                <div className="@xl:flex-1 flex w-full flex-col gap-6">
                    <div className="flex flex-col gap-5 text-gray-700">
                        <h2>
                            <img
                                src={GivebutterPlusLogo}
                                className="h-auto w-36"
                                alt="Givebutter Plus logo"
                                width={144}
                                height={16}
                                draggable={false}
                            />
                        </h2>
                        <div className="flex flex-col gap-3">
                            <h3 className="text-3xl font-medium text-gray-900">
                                {isDataClean && !isTotalReviewed ? (
                                    <span>Your data looks squeaky clean!</span>
                                ) : (
                                    <span>Stay on top of your data</span>
                                )}
                            </h3>
                            <p>
                                Keep the rest of your contact records clean and correct with data
                                hygiene. We verify addresses, recognize mistakes, and help you
                                maintain spotless donor information.
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <a href={PLUS_MARKETING_LINK} target="_blank" rel="noreferrer">
                            <Button variant="outline" color="neutral">
                                Learn more
                            </Button>
                        </a>
                        <Link to={`/accounts/${accountId}/settings/about/subscription`}>
                            <Button variant="solid">Upgrade to Givebutter Plus</Button>
                        </Link>
                    </div>
                </div>
                <div className="@xl:block relative hidden flex-1">
                    <img
                        src={DataHygieneFeature}
                        alt="Data hygiene illustration"
                        draggable={false}
                        className="ml-auto h-auto w-full max-w-[23rem]"
                    />
                </div>
            </div>

            <div className="bg-gray-25 flex w-full flex-col">
                <div className="flex w-full min-w-0 items-center gap-4 p-4">
                    <Meter.Root
                        aria-label="Trial Progress"
                        min={0}
                        max={total}
                        value={progress}
                        type="circle"
                        variant="success"
                        size="sm"
                    >
                        <Meter.MeterCircularContainer>
                            <Meter.MeterCircularTrack />
                            <Meter.MeterCircularIndicator />
                        </Meter.MeterCircularContainer>
                    </Meter.Root>
                    <div className="flex-grow text-sm text-gray-600">
                        <h2 className="font-medium text-gray-700">Contacts reviewed</h2>

                        <div className="flex flex-col gap-1">
                            <p>
                                Your free trial allows us to review up to{' '}
                                {maskWholeNumber(visibleTotal)} of your contacts.
                            </p>

                            <div
                                className={
                                    'flex items-center gap-3 text-sm font-medium text-gray-700'
                                }
                            ></div>
                        </div>
                    </div>
                </div>
            </div>
        </Box>
    ) : null;
};

export { TrialProgressCard };
